import {
  queriesNotifications,
  queriesPayeeRows,
  queriesPayerEngagements
} from "@query";
import {
  useWSModalCustom,
  WSDataItemProps,
  WSElementColorsType,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { IRequirementResponse } from "@wingspanhq/payments/dist/interfaces/api/requirement";
import { ISharedFileRequestResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import type { insuranceMonitorApiSpec } from "@wingspanhq/wingspan-api-types";
import { WSQueryCache, WSQueryKey } from "@ws-react-query";
import { useCallback, useMemo } from "react";

import { QUERY_BACKGROUND_CHECKS_REQUEST } from "../../query/backgroundChecks/queries/useQueryBackgroundChecksRequest";
import { getPayerRowCache } from "../../query/cache/getPayerRowCache";
import { QUERY_IDENTITY_VERIFICATION_REQUEST } from "../../query/identityVerification/keys";
import { QUERY_INSURANCE_COVERAGE_MONITOR } from "../../query/insuranceCoverageMonitor/queries/useQueryInsuranceCoverageMonitor";
import {
  QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID,
  QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL
} from "../../query/payeeEngagements/keys";
import {
  QUERY_REQUIREMENT,
  QUERY_REQUIREMENTS_FULL_LIST,
  QUERY_REQUIREMENTS_FULL_LIST_SIZE
} from "../../query/requirements/keys";
import { QUERY_LIST_RESOURCE_EVENTS } from "../../query/resourceEvents/queries/useQueryListEvents";
import { useQueryPayerRows } from "../../query/search/payer/queries/useQueryPayerRows";
import { QUERY_PAYER_ROWS_ALL } from "../../query/search/payer/queries/useQueryPayerRowsAll";
import { QUERY_SHARED_FILE } from "../../query/sharedFile/keys";
import { QUERY_SIGNED_DOCUMENT } from "../../query/signedDocuments/keys";
import { DataItemProps } from "../../shared/components/DataView";
import { IS_LOCAL_ENV } from "../../shared/constants/environment";
import { QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS } from "../Onboarding/queries/useQueryAllPayersWithEngagements";
import { useVersionQuickReferenceModal } from "../RequirementDefinitions/components/VersionQuickReferenceModal";
import { getRequirementDefinitionDataItemProps } from "../RequirementDefinitions/utils";

import { ModalRequirementHistory } from "./components/ModalRequirementHistory";
import { RequirementVersionLabel } from "./components/RequirementVersionLabel";
import { extendRequirementOnboardingToolbarActions } from "./helpers/extendRequirementOnboardingToolbarActions";
import { extendRequirementStatusCell } from "./helpers/extendRerquirementStatusCell";
import { getRequirementExpirationWidget } from "./helpers/getRequirementExpirationWidget";
import { getRequirementHistoryWidget } from "./helpers/getRequirementHistoryWidget";
import { getRequirementOverrideExpirationAlert } from "./helpers/getRequirementOverrideExpirationAlert";
import { getRequirementOverrideStatusAlert } from "./helpers/getRequirementOverrideStatusAlert";
import { getRequirementProgressWidget } from "./helpers/getRequirementProgressWidget";
import { useRequirementDefaultActions } from "./hooks/useRequirementDefaultActions";
import { useBackgroundChecksRequirementTableConfig } from "./modules/BackgroundChecks";
import { useIdentityVerificationRequirementConfig } from "./modules/IdentityVerification";
import { useInsuranceCoverageRequirementTableConfig } from "./modules/InsuranceCoverage";
import { selectorPolicyTypesFromSettings } from "./modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";
import { usePayoutMethodRequirementTableConfig } from "./modules/PayoutMethod";
import { useRegistrationRequirementTableConfig } from "./modules/Registration";
import { useSharedFileRequirementTableConfig } from "./modules/SharedFile";
import { useSignatureRequirementTableConfig } from "./modules/Signature";
import { useTaxVerificationRequirementTableConfig } from "./modules/TaxVerification";
import { selectorRequirementEngagementsSize } from "./selectors/selectorRequirementEngagementsSize";
import {
  ContextItem,
  RequirementAction,
  RequirementContextType,
  RequirementStatusViewProps,
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig,
  TableCellProps
} from "./types";
import {
  ALL_REQUIREMENT_EVENTS,
  getRequirementPayerDataItemProps,
  getRequirementPayerTableCellProps,
  RequirementEvent
} from "./utils";

type InsuranceCoverageMonitor =
  insuranceMonitorApiSpec.InsuranceCoverageMonitor;

export type RequirementsConfigParams = {
  requirements: Array<RequirementTableItemData>;
  contextType: RequirementContextType;
  context?: ContextItem;
};

export type RequirementsConfigResult = ReturnType<typeof useRequirementsConfig>;

export const useRequirementsConfig = (props: RequirementsConfigParams) => {
  const { contextType, context = {}, requirements } = props;
  const modalHistory = useWSModalCustom(ModalRequirementHistory);

  const callback: RequirementTableCallback = useCallback(
    (eventName, requirement) => {
      const eventsWithNoSideEffects: RequirementEvent[] = [
        ALL_REQUIREMENT_EVENTS.DownloadAsPayee,
        ALL_REQUIREMENT_EVENTS.DownloadAsPayer
      ];

      if (!eventsWithNoSideEffects.includes(eventName)) {
        const listOfQueries: WSQueryKey[] = [];
        const addToInvalidations = (key: WSQueryKey) => listOfQueries.push(key);

        if ("dataSourceId" in requirement && requirement.dataSourceId) {
          switch (requirement.requirementType) {
            case RequirementType.Signature:
              addToInvalidations([
                QUERY_SIGNED_DOCUMENT,
                requirement.dataSourceId
              ]);
              break;
            case RequirementType.SharedFile:
              addToInvalidations([QUERY_SHARED_FILE, requirement.dataSourceId]);
              break;
            case RequirementType.BackgroundCheck:
              addToInvalidations([
                QUERY_BACKGROUND_CHECKS_REQUEST,
                requirement.dataSourceId
              ]);
              break;
            case RequirementType.IdentityVerification:
              addToInvalidations([
                QUERY_IDENTITY_VERIFICATION_REQUEST,
                requirement.dataSourceId
              ]);
              break;
            case RequirementType.InsuranceCoverage:
              addToInvalidations([
                QUERY_INSURANCE_COVERAGE_MONITOR,
                requirement.dataSourceId
              ]);
              break;
          }
        }

        addToInvalidations(queriesNotifications.listNotifications.queryKeyBase);
        addToInvalidations([QUERY_REQUIREMENTS_FULL_LIST]);
        addToInvalidations([QUERY_REQUIREMENTS_FULL_LIST_SIZE]);
        addToInvalidations([QUERY_REQUIREMENT, requirement.requirementId]);

        addToInvalidations([QUERY_PAYER_ROWS_ALL]);
        addToInvalidations(queriesPayeeRows.searchPayeeRows.queryKeyBase);

        addToInvalidations(
          queriesPayerEngagements.getPayerEngagement.queryKeyBase
        );
        addToInvalidations(
          queriesPayerEngagements.listPayerEngagements.queryKeyBase
        );
        addToInvalidations([QUERY_ALL_ENGAGEMENTS_BY_PAYER_IDS]);
        addToInvalidations([QUERY_PAYEE_ENGAGEMENTS_LIST_BY_PAYEE_ID_ALL]);
        addToInvalidations([QUERY_PAYEE_ENGAGEMENT_BY_PAYEE_ID]);

        addToInvalidations([QUERY_LIST_RESOURCE_EVENTS]);

        if (IS_LOCAL_ENV) {
          console.log(
            "Invalidating queries",
            JSON.stringify(listOfQueries, null, 2)
          );
        }

        listOfQueries.forEach(key => WSQueryCache.invalidateQueries(key));
      }
    },
    []
  );

  const requirementDefaultActions = useRequirementDefaultActions(
    contextType,
    callback
  );

  const signatureConfig = useSignatureRequirementTableConfig(
    callback,
    requirements
  );
  const sharedFileConfig = useSharedFileRequirementTableConfig(
    callback,
    requirements
  );
  const backgroundCheckConfig = useBackgroundChecksRequirementTableConfig(
    callback,
    requirements
  );
  const identityVerificationConfig = useIdentityVerificationRequirementConfig(
    callback,
    requirements
  );
  const insuranceCoverageConfig = useInsuranceCoverageRequirementTableConfig(
    callback,
    requirements
  );
  const registrationConfig = useRegistrationRequirementTableConfig(
    callback,
    requirements
  );
  const taxVerificationConfig = useTaxVerificationRequirementTableConfig(
    callback,
    requirements
  );
  const payoutMethodConfig = usePayoutMethodRequirementTableConfig(
    callback,
    requirements
  );

  const config: RequirementTableRowConfig = {
    [RequirementType.Signature]: signatureConfig,
    [RequirementType.SharedFile]: sharedFileConfig,
    [RequirementType.BackgroundCheck]: backgroundCheckConfig,
    [RequirementType.IdentityVerification]: identityVerificationConfig,
    [RequirementType.InsuranceCoverage]: insuranceCoverageConfig,
    [RequirementType.Registration]: registrationConfig,
    [RequirementType.TaxVerification]: taxVerificationConfig,
    [RequirementType.PayoutMethod]: payoutMethodConfig
  };

  // Centralized payer row queries for all requirements
  const payerIds = useMemo(() => {
    const uniquePayerIds = [
      ...new Set(
        requirements.filter(req => !!req.payerId).map(req => req.payerId!)
      )
    ];
    return uniquePayerIds;
  }, [requirements]);
  const queryPayerRows = useQueryPayerRows({
    filter: {
      payerId: { in: payerIds }
    }
  });

  const isLoading =
    Object.values(config).some(({ isLoading }) => isLoading) ||
    queryPayerRows.isLoading;
  const dataUpdatedAt = Object.values(config)
    .map(({ dataUpdatedAt }) => dataUpdatedAt)
    .join(", ");

  const getStatusCellProps = (item: RequirementTableItemData) => {
    const statusCell = config[item.requirementType].statusCell[contextType](
      item as IRequirementResponse,
      context
    );

    return extendRequirementStatusCell(statusCell, item, contextType);
  };

  const getToolbarActions = (requirement: RequirementTableItemData) => {
    const customActions =
      config[requirement.requirementType].toolbarActions?.[contextType](
        requirement,
        context
      ) || [];

    const dataSource = config[requirement.requirementType].getAdditionalData?.(
      requirement,
      context
    );

    const status = config[requirement.requirementType].statusCell[contextType](
      requirement,
      context
    ) as RequirementStatusViewProps;

    const { extendToolbarActions } = requirementDefaultActions(
      requirement,
      context,
      dataSource,
      status
    );

    return extendToolbarActions(customActions);
  };

  return {
    isActionRequired: (item: RequirementTableItemData) => {
      return config[item.requirementType].isActionRequired?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getRequirementDetailsWidgets(item: RequirementTableItemData) {
      return config[item.requirementType].requirementDetailsWidgets?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementProgressSteps(item: RequirementTableItemData) {
      return (
        config[item.requirementType].requirementDetailsProgressWidget?.[
          contextType
        ](item as IRequirementResponse, context) ?? []
      );
    },
    getRequirementDetailsProgressWidget(
      item: RequirementTableItemData,
      showTitle: boolean = true
    ) {
      const steps = config[
        item.requirementType
      ].requirementDetailsProgressWidget?.[contextType](
        item as IRequirementResponse,
        context
      );

      return getRequirementProgressWidget(steps, item, showTitle);
    },
    getRequirementExpirationWidget(item: RequirementTableItemData) {
      return getRequirementExpirationWidget(item);
    },
    getRequirementDetailsHistoryWidget(item: RequirementTableItemData) {
      return getRequirementHistoryWidget(item);
    },
    getRequirementInstructionsWidgets(item: RequirementTableItemData) {
      return config[item.requirementType].requirementInstructionsWidgets?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementInstructions(item: RequirementTableItemData) {
      return config[item.requirementType].requirementInstructions?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getRequirementName(item: RequirementTableItemData) {
      return config[item.requirementType].requirementName?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getToolbarActions,
    getOnboardingToolbarActions(item: RequirementTableItemData) {
      return (onClose: () => void) => {
        let actions: RequirementAction[] = [];

        const isMainActionActive = (action: RequirementAction) =>
          action.mainAction &&
          action.visible !== false &&
          action.hidden !== true &&
          action.noOnboarding !== true &&
          action.buttonKind;

        const originalActions = getToolbarActions(item);

        actions = extendRequirementOnboardingToolbarActions(
          item,
          originalActions,
          onClose
        );

        const isMainActionExist = actions.some(isMainActionActive);
        const activeActions = actions.filter(
          action =>
            action.noOnboarding !== true &&
            action.visible !== false &&
            action.hidden !== true
        );

        return activeActions
          .map((action, index, all) => ({
            ...action,
            buttonKind: action.buttonKind
              ? isMainActionExist
                ? isMainActionActive(action)
                  ? action.buttonKind
                  : "Link"
                : action.buttonKind
              : undefined
          }))
          .map(action =>
            isLoading
              ? {
                  ...action,
                  visible: false
                }
              : action
          ) as RequirementAction[];
      };
    },
    getRequirementStatusAlert(requirement: RequirementTableItemData) {
      const approvalFlowAlert = getRequirementOverrideStatusAlert({
        requirement,
        contextType,
        onOpenHistory() {
          modalHistory.open({
            requirement
          });
        }
      });

      if (approvalFlowAlert) {
        return approvalFlowAlert;
      }

      return config[requirement.requirementType].requirementStatusAlert?.[
        contextType
      ](requirement as IRequirementResponse, context);
    },
    getRequirementExpirationAlerts(requirement: RequirementTableItemData) {
      const statusCell = config[requirement.requirementType].statusCell[
        contextType
      ](requirement, context);

      const expirationAlerts = getRequirementOverrideExpirationAlert({
        requirement,
        contextType,
        currentStatus: {
          pill: statusCell.pill!,
          secondaryText: statusCell.secondaryText as string,
          secondaryTextColor:
            statusCell.secondaryTextColor as WSElementColorsType
        }
      });

      return expirationAlerts;
    },
    getRequirementBasicInformation(
      item: RequirementTableItemData
    ): DataItemProps[] {
      const customBasicInfo = config[
        item.requirementType
      ].requirementBasicInformation?.[contextType](item, context);

      if (customBasicInfo) {
        return customBasicInfo;
      }

      const statusProps = getStatusCellProps(item);

      const dataSource = config[item.requirementType].getAdditionalData?.(
        item,
        context
      ) as ISharedFileRequestResponse | InsuranceCoverageMonitor | undefined;

      // For Payer context, show "Clients" with payer info
      // For Payee context, show "Engagements" count
      const clientOrEngagementItem =
        contextType === RequirementContextType.Payer
          ? getRequirementPayerDataItemProps(
              item,
              getPayerRowCache(item.payerId)
            )
          : {
              label: "Engagements",
              value: selectorRequirementEngagementsSize(item)
            };

      return [
        getRequirementDefinitionDataItemProps({
          type: item.requirementType,
          contextType:
            dataSource && "sharedFileRequestId" in dataSource
              ? dataSource.contextType
              : undefined,
          policyType:
            dataSource && "insuranceCoverageMonitorId" in dataSource
              ? selectorPolicyTypesFromSettings(dataSource.settings)
              : undefined
        }),
        clientOrEngagementItem,
        {
          label: {
            text: "Status"
          },
          value: "",
          hideValue: true,
          ...(statusProps as WSDataItemProps)
        },
        ...(item.requirementDefinitionVersion &&
        item.requirementDefinitionVersion > 1 &&
        contextType === RequirementContextType.Payee
          ? [
              {
                label: {
                  text: "Version"
                },
                value: "",
                hideValue: true,
                children: (
                  <RequirementVersionLabel
                    requirementDefinitionId={item.requirementDefinitionId!}
                    size="M"
                    version={item.requirementDefinitionVersion}
                  />
                )
              }
            ]
          : [])
      ];
    },
    getRequirementDetails(item: RequirementTableItemData) {
      return config[item.requirementType].requirementDetails?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getRequirementAdditionalContent(item: RequirementTableItemData) {
      return config[item.requirementType].requirementAdditionalContent?.[
        contextType
      ](item as IRequirementResponse, context);
    },
    getNameCellProps(item: RequirementTableItemData) {
      const baseNameProps = config[item.requirementType].nameCell[contextType](
        item as IRequirementResponse,
        context
      );

      return {
        ...baseNameProps,
        secondaryText:
          contextType === RequirementContextType.Payee ? (
            <RequirementVersionLabel
              defaultSecondaryText={baseNameProps.secondaryText}
              requirementDefinitionId={item.requirementDefinitionId!}
              version={item.requirementDefinitionVersion}
            />
          ) : undefined
      } as TableCellProps;
    },
    getTypeCellProps(item: RequirementTableItemData) {
      return config[item.requirementType].typeCell[contextType](
        item as IRequirementResponse,
        context
      );
    },
    getStatusCellProps(item: RequirementTableItemData) {
      return getStatusCellProps(item);
    },
    getClientsCellProps(item: RequirementTableItemData) {
      const customClientsCellProps = config[item.requirementType].clientsCell?.[
        contextType
      ](item as IRequirementResponse, context);

      if (customClientsCellProps) {
        return customClientsCellProps;
      }

      // Default implementation
      const payer = getPayerRowCache(item.payerId);
      return getRequirementPayerTableCellProps(item, payer);
    },
    getEngagementsCellProps(item: RequirementTableItemData) {
      return config[item.requirementType].engagementsCell?.[contextType](
        item as IRequirementResponse,
        context
      );
    },
    renderRowMenuActions(item: RequirementTableItemData) {
      return getToolbarActions(item).filter(action => action.noTable !== true);
    },
    isLoading,
    dataUpdatedAt,
    getContextType(item: RequirementTableItemData) {
      const dataSource = config[item.requirementType].getAdditionalData?.(
        item as IRequirementResponse,
        context
      ) as ISharedFileRequestResponse | undefined;

      return dataSource?.contextType;
    },
    getPolicyType(item: RequirementTableItemData) {
      const dataSource = config[item.requirementType].getAdditionalData?.(
        item as IRequirementResponse,
        context
      ) as InsuranceCoverageMonitor | undefined;

      return selectorPolicyTypesFromSettings(dataSource?.settings);
    },
    getAdditionalData(item: RequirementTableItemData) {
      return config[item.requirementType].getAdditionalData?.(
        item as IRequirementResponse,
        context
      );
    }
  } as const;
};
